import React, { useState, useEffect, useCallback, useRef } from 'react';
import './TurnSelectionStep.css';
import BookingCalendar from "../BookingCalendar/BookingCalendar";
import AvailableTurns from "../AvailableTurns/AvailableTurns";
import authenticate from '../../utils/authenticate';
import { parse, isValid, isSameDay } from 'date-fns';
import { ApiResponse, DayOff, Turn } from '../../models/types';
import { get } from '../../services/api';
import { config } from "../../config/environment";
import { isDateBookable, isTurnBookable } from '../../utils/bookableDate';

interface TurnSelectionStepProps {
    shopId: number;
    roomNum: number;
    selectedDate: Date | null;
    selectedTurn: Turn | null;
    onDateSelect: (date: Date) => void;
    onTurnSelect: (turn: Turn) => void;
    showCalendar: boolean;
    setShowCalendar: React.Dispatch<React.SetStateAction<boolean>>;
}

const TurnSelectionStep: React.FC<TurnSelectionStepProps> = ({ shopId, roomNum, selectedDate, selectedTurn, onDateSelect, onTurnSelect, showCalendar, setShowCalendar }) => {
    const [firstLoad, setFirstLoad] = useState(true);
    const [dateShown, setDateShown] = useState<Date>(new Date());
    const [bankHolidays, setBankHolidays] = useState<Date[]>([]);
    const [availableTurns, setAvailableTurns] = useState<Turn[]>([]);
    const [turnsForSelectedDate, setTurnsForSelectedDate] = useState<Turn[]>([]);
    const dateShownRef = useRef(dateShown);

    useEffect(() => {
        dateShownRef.current = dateShown;
    }, [dateShown]);

    const fetchData = useCallback(async (date: Date | null) => {
        const targetDate = date ?? dateShownRef.current;
        const token = await authenticate();
        if (!token) {
            alert('Failed to authenticate');
            return;
        }

        setDateShown(targetDate);

        const year = targetDate.getFullYear();
        const month = targetDate.getMonth() + 1;
        const formattedMonth = month < 10 ? `0${month}` : month;
        const url = `${config.API_BASE_URL}/clientapi/buy/step1/shop/${shopId}/room/${roomNum}/${year}/${formattedMonth}`;
        await get(url, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
        .then((response: ApiResponse) => {
            const data = response.message;

            setBankHolidays(data.days_off.map((dayOff: DayOff) => {
                const dateParsed = parse(dayOff.date, 'yyyy-MM-dd', new Date());
                return isValid(dateParsed) ? dateParsed : null;
            }).filter((day: Date | null): day is Date => day !== null));

            setAvailableTurns(data.free_turns
                .map((turn: Turn) => ({
                    ...turn,
                    ask_shop: typeof turn.ask_shop === 'string' ? turn.ask_shop === "1" : Boolean(turn.ask_shop)
                }))
                .filter(isTurnBookable));
        })
        .catch(error => {
            console.error('Error fetching available turns:', error);
        });
    }, [shopId, roomNum]);

    useEffect(() => {
        fetchData(selectedDate);
    }, [selectedDate, fetchData]);

    const handleDateChange = (date: Date) => {
        if (!isDateBookable(date)) {
            return;
        }
        setFirstLoad((prev) => !prev);
        onDateSelect(date);
    };

    const handleDateSelect = (date: Date) => {
        if (!isDateBookable(date)) {
            return;
        }
        const turnsForDate = availableTurns.filter(turn => isSameDay(parse(turn.booking_date, 'yyyy-MM-dd', new Date()), date));
        setTurnsForSelectedDate(turnsForDate);
        setShowCalendar(false);
        onDateSelect(date);
    };

    const handleTurnSelect = (turn: Turn) => {
        onTurnSelect(turn);
    };

    return (
        <div className="flip-container">
            <div className={`flipper ${showCalendar ? (firstLoad ? '' : 'rotateAgain') : 'rotate'}`}>
                <div className="front">
                    <BookingCalendar showThreeSlots={shopId !== 7} availableTurns={availableTurns} bankHolidays={bankHolidays} selectedDate={selectedDate} dateShown={dateShown} onDateSelect={handleDateSelect} onDateChange={handleDateChange} onMonthChange={fetchData} />
                </div>
                <div className="back">
                    <div>
                        <AvailableTurns turns={turnsForSelectedDate} selectedTurn={selectedTurn} onTurnSelect={handleTurnSelect} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TurnSelectionStep;
